import { useEffect, useMemo, useState } from "react";
import {
    Box,
    Typography,
    CircularProgress,
    IconButton,
    Button,
    Stack,
    Pagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import RestoreFromTrashIcon from "@mui/icons-material/RestoreFromTrash";
import Grid from "@mui/material/Grid";
import { useNavigate } from "react-router-dom";

import { AppPage, AppHeader, AppCard } from "../../../layout/layout";
import { recipeService, RecipeViewDto } from "../../../services/recipe-service";

type ActionKind = "soft" | "hard" | "restore";

export default function RecipeList() {
    const [recipes, setRecipes] = useState<RecipeViewDto[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(true);

    const [page, setPage] = useState(1);
    const pageSize = 10;

    const navigate = useNavigate();

    // confirm dialog
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [action, setAction] = useState<ActionKind>("soft");
    const [target, setTarget] = useState<RecipeViewDto | null>(null);

    const pageCount = useMemo(
        () => Math.max(1, Math.ceil(totalCount / pageSize)),
        [totalCount]
    );

    useEffect(() => {
        if (page > pageCount) setPage(pageCount);
    }, [page, pageCount]);

    const load = async (targetPage = page) => {
        setLoading(true);
        try {
            const res = await recipeService.getPaged(targetPage, pageSize);
            console.log("recipes", res);
            setRecipes(res.items);
            setTotalCount(res.totalCount);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load(page);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);

    const ask = (kind: ActionKind, recipe: RecipeViewDto) => {
        setAction(kind);
        setTarget(recipe);
        setConfirmOpen(true);
    };

    const closeConfirm = () => setConfirmOpen(false);

    const doAction = async () => {
        if (!target) return;

        try {
            if (action === "soft") {
                await recipeService.delete(target.id); // soft delete endpoint
            } else if (action === "hard") {
                await recipeService.hardDelete(target.id); // hard delete endpoint
            } else {
                await recipeService.restore(target.id); // restore endpoint
            }

            // if last item on page removed (hard delete), go back a page
            const wouldRemoveRow = action === "hard";
            const isLastRow = wouldRemoveRow && recipes.length === 1 && page > 1;
            const nextPage = isLastRow ? page - 1 : page;

            setConfirmOpen(false);
            await load(nextPage);
            if (nextPage !== page) setPage(nextPage);
        } catch (err) {
            console.error(err);
            setConfirmOpen(false);
            await load(page);
        }
    };

    const confirmTitle =
        action === "soft"
            ? "Soft delete recipe?"
            : action === "hard"
                ? "Delete permanently?"
                : "Restore recipe?";

    const confirmText =
        action === "soft"
            ? "This will disable the recipe (you can restore it later)."
            : action === "hard"
                ? "This will permanently delete the recipe and all related data. This cannot be undone."
                : "This will restore the recipe and make it active again.";

    return (
        <AppPage>
            <AppHeader title="Recipes" subtitle="Manage your recipes" />

            <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => navigate("/admin/recipes/new")}
                >
                    Create recipe
                </Button>
            </Box>

            <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                    <AppCard>
                        {loading ? (
                            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                                <CircularProgress />
                            </Box>
                        ) : recipes.length === 0 ? (
                            <Typography color="text.secondary">No recipes yet.</Typography>
                        ) : (
                            <>
                                <Stack spacing={1}>
                                    {recipes.map((r) => {
                                        const inactive = !r.isActive;

                                        return (
                                            <Box
                                                key={r.id}
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "space-between",
                                                    p: 1.5,
                                                    borderRadius: 1,
                                                    "&:hover": { backgroundColor: "action.hover" },
                                                    opacity: inactive ? 0.65 : 1, // subtle disabled feel
                                                }}
                                            >
                                                <Box>
                                                    <Typography
                                                        fontWeight={600}
                                                        color={inactive ? "text.disabled" : "text.primary"}
                                                    >
                                                        {r.title}
                                                    </Typography>
                                                    {r.description && (
                                                        <Typography
                                                            variant="body2"
                                                            color={inactive ? "text.disabled" : "text.secondary"}
                                                        >
                                                            {r.description}
                                                        </Typography>
                                                    )}
                                                </Box>

                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                    {/* Edit */}
                                                    <Tooltip title={inactive ? "Restore to edit" : "Edit"}>
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                disabled={inactive}
                                                                onClick={() => navigate(`/admin/recipes/${r.id}/edit`)}
                                                            >
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>

                                                    {/* Soft delete OR Restore */}
                                                    {inactive ? (
                                                        <Tooltip title="Restore">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => ask("restore", r)}
                                                            >
                                                                <RestoreFromTrashIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    ) : (
                                                        <Tooltip title="Soft delete">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => ask("soft", r)}
                                                            >
                                                                <DeleteOutlineIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}

                                                    {/* Hard delete always available */}
                                                    <Tooltip title="Delete permanently">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => ask("hard", r)}
                                                        >
                                                            <DeleteForeverIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </Stack>

                                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                                    <Pagination
                                        count={pageCount}
                                        page={page}
                                        onChange={(_, value) => setPage(value)}
                                        shape="rounded"
                                    />
                                </Box>
                            </>
                        )}
                    </AppCard>
                </Grid>
            </Grid>

            <Dialog open={confirmOpen} onClose={closeConfirm}>
                <DialogTitle>{confirmTitle}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {target ? (
                            <>
                                <strong>{target.title}</strong>
                                <br />
                                {confirmText}
                            </>
                        ) : (
                            confirmText
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeConfirm}>Cancel</Button>
                    <Button
                        onClick={doAction}
                        variant="contained"
                        color={action === "hard" ? "error" : "primary"}
                    >
                        {action === "soft"
                            ? "Soft delete"
                            : action === "hard"
                                ? "Delete permanently"
                                : "Restore"}
                    </Button>
                </DialogActions>
            </Dialog>
        </AppPage>
    );
}
